'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';

import { fetchRealties } from '../../api/realty/realty-api';
import {
  constrainRealtyFilterCapabilities,
  parseRealtySearchParams,
  serializeRealtyFilter,
} from '../../utils/realty-filter-contract';
import { buildRealtyListingQuery } from '../../utils/realty-utils';
import { matchingRealtyListingSeed, type RealtyListingSeed } from '../../utils/realty-listing-seed';
import { RealtyFilterSortDesktop } from './filter/realty-filter-sort-desktop';
import { RealtyCard } from './realty-card';
import { RealtyCardSkeleton } from './realty-card/realty-card-skeleton';
import styles from './realty-listing.module.css';

type RealtiesListProps = {
  initialListing?: RealtyListingSeed;
  supportsUnderConstruction?: boolean;
};

const INITIAL_SKELETON_COUNT = 5;

export const RealtiesList = ({
  initialListing,
  supportsUnderConstruction = false,
}: RealtiesListProps) => {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();
  const searchParams = useSearchParams();
  const appliedFilter = constrainRealtyFilterCapabilities(parseRealtySearchParams(searchParams), {
    underConstruction: supportsUnderConstruction,
  });
  const appliedFilterKey = serializeRealtyFilter(appliedFilter);
  const seed = matchingRealtyListingSeed(initialListing, locale, appliedFilterKey);

  const realtiesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REALTIES, locale, appliedFilterKey],
    queryFn: async ({ pageParam = 1, signal }) => {
      return await fetchRealties({
        locale,
        signal,
        query: buildRealtyListingQuery(appliedFilter, pageParam, supportsUnderConstruction),
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
    initialData: seed?.realties ? { pages: [seed.realties], pageParams: [1] } : undefined,
    initialDataUpdatedAt: seed?.fetchedAt,
  });

  const realties = realtiesQuery.data?.pages.flatMap(page => page.data) ?? [];

  const realtiesCount = realtiesQuery.data?.pages?.[0]?.meta?.total;
  const hasRealties = realties.length > 0;
  const countLabel =
    typeof realtiesCount !== 'number' || realtiesCount === 0
      ? t('text1419')
      : `${realtiesCount} ${
          realtiesCount === 1 ? t('text1425') : realtiesCount < 5 ? t('text1426') : t('text1420')
        }`;
  const initialError = realtiesQuery.isError && !hasRealties;
  const initialLoading = realtiesQuery.isPending && !hasRealties;
  const empty = realtiesQuery.isSuccess && !hasRealties;

  return (
    <section
      className={styles.results}
      data-eli-component="realty-listing-results"
      data-state={initialLoading ? 'loading' : initialError ? 'error' : empty ? 'empty' : 'ready'}
      aria-busy={realtiesQuery.isFetching}
    >
      <div className={styles.resultsHeader} data-eli-part="listing-results-header">
        <div className={styles.count} aria-live="polite" data-eli-part="listing-count">
          {initialLoading ? (
            <Skeleton
              aria-hidden="true"
              className="h-[21px] w-[180px] bg-border motion-reduce:animate-none xl:h-[27px] xl:w-[218px]"
            />
          ) : (
            countLabel
          )}
        </div>
        <div className={styles.desktopSort}>
          <RealtyFilterSortDesktop />
        </div>
      </div>

      <div className={styles.cards} data-eli-part="listing-cards">
        {initialError && (
          <div className={styles.state} role="alert" data-eli-part="listing-error">
            <p>{t('text530')}</p>
            <Button
              type="button"
              className={styles.stateAction}
              onClick={() => void realtiesQuery.refetch()}
              isLoading={realtiesQuery.isRefetching}
            >
              {t('text595')}
            </Button>
          </div>
        )}

        {initialLoading && (
          <>
            <div className="sr-only" role="status" data-eli-part="listing-loading">
              {t('text439')}
            </div>
            {Array.from({ length: INITIAL_SKELETON_COUNT }, (_, index) => (
              <RealtyCardSkeleton key={`realty-card-skeleton-${index}`} />
            ))}
          </>
        )}

        {empty && (
          <div className={styles.state} role="status" data-eli-part="listing-empty">
            <p>{t('text1968')}</p>
          </div>
        )}

        {hasRealties &&
          realties.map((realty, index) => (
            <RealtyCard realty={realty} preload={index === 0} key={realty.id} />
          ))}
      </div>

      {hasRealties && realtiesQuery.isFetchNextPageError && (
        <div className={styles.state} role="alert" data-eli-part="listing-more-error">
          <p>{t('text530')}</p>
          <Button
            type="button"
            className={styles.stateAction}
            onClick={() => void realtiesQuery.fetchNextPage()}
            isLoading={realtiesQuery.isFetchingNextPage}
          >
            {t('text595')}
          </Button>
        </div>
      )}

      {hasRealties && realtiesQuery.hasNextPage && !realtiesQuery.isFetchNextPageError && (
        <div className={styles.loadMoreWrapper} data-eli-part="listing-load-more">
          <Button
            type="button"
            onClick={() => {
              if (!realtiesQuery.isFetchingNextPage) void realtiesQuery.fetchNextPage();
            }}
            disabled={realtiesQuery.isFetchingNextPage}
            isLoading={realtiesQuery.isFetchingNextPage}
            variant="secondary"
            className={styles.loadMore}
          >
            {t('text338')}
          </Button>
        </div>
      )}
    </section>
  );
};
